import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './components/LanguageSwitcher.jsx';

// ── Constants ─────────────────────────────────────────────────────────────────
const LARGE_SITE_THRESHOLD = parseInt(import.meta.env.VITE_MAX_URLS_FROM_SITEMAP) || 500;

// ── Slug / path-prefix helpers ────────────────────────────────────────────────
function extractPathPrefixes(rawUrl) {
  try {
    const url = new URL(rawUrl);
    const segments = url.pathname.replace(/\/$/, '').split('/').filter(Boolean);
    if (segments.length === 0) return ['/'];
    const prefixes = [];
    for (let i = segments.length; i >= 1; i--) {
      prefixes.push('/' + segments.slice(0, i).join('/'));
    }
    prefixes.push('/');
    return prefixes;
  } catch {
    return [];
  }
}

// ── Socket factory ────────────────────────────────────────────────────────────
function createSocket() {
  return io({ path: '/socket.io', transports: ['websocket', 'polling'] });
}

// ── Custom hook: useConversionJob ─────────────────────────────────────────────
function useConversionJob() {
  const [jobId, setJobId]                     = useState(null);
  const [stage, setStage]                     = useState('input');
  const [queuePosition, setQueuePosition]     = useState(0);
  const [discoveredUrls, setDiscoveredUrls]   = useState([]);
  const [totalDiscovered, setTotalDiscovered] = useState(0);
  const [selectedUrls, setSelectedUrls]       = useState(() => new Set());
  const [progress, setProgress]               = useState({});
  const [discoveryLogs, setDiscoveryLogs]     = useState([]);
  const [conversionLogs, setConversionLogs]   = useState([]);
  const [error, setError]                     = useState(null);
  const [socket, setSocket]                   = useState(null);

  useEffect(() => {
    const s = createSocket();
    setSocket(s);
    return () => s.close();
  }, []);

  const jobIdRef = useRef(null);
  useEffect(() => { jobIdRef.current = jobId; }, [jobId]);

  useEffect(() => {
    if (!socket) return;
    const onReconnect = () => {
      if (jobIdRef.current) socket.emit('register', jobIdRef.current);
    };
    socket.io.on('reconnect', onReconnect);
    return () => socket.io.off('reconnect', onReconnect);
  }, [socket]);

  const progressHandlerRef = useRef(null);

  useEffect(() => {
    if (!socket || !jobId) return;
    socket.emit('register', jobId);

    const handleProgress = (data) => {
      setProgress(data);
      if (data.message) {
        const entry = { timestamp: new Date().toISOString(), message: data.message, status: data.status };
        if (data.stage === 'conversion' || data.stage === 'archiving' || data.stage === 'completed') {
          setConversionLogs(prev => [...prev, entry]);
        } else {
          setDiscoveryLogs(prev => [...prev, entry]);
        }
      }
      if (data.stage === 'queued' && data.status === 'active') { setQueuePosition(0); setStage('discovery'); return; }
      if (data.stage === 'queued')                              { setQueuePosition(data.position ?? 0); return; }
      if (data.stage === 'discovery' && data.status === 'completed' && data.urls) {
        const urls = data.urls;
        setDiscoveredUrls(urls);
        setTotalDiscovered(data.totalUrls ?? urls.length);
        const truncated = !!data.truncated;
        setSelectedUrls(truncated ? new Set() : new Set(urls));
        setStage(truncated ? 'large-site-warning' : 'selection');
      }
      if (data.stage === 'conversion' && data.status === 'processing') setStage('processing');
      if (data.stage === 'completed')                                   setStage('completed');
      if (data.stage === 'error') { setError(data.message); setStage('input'); }
    };

    progressHandlerRef.current = handleProgress;
    socket.on('progress', handleProgress);
    return () => {
      socket.off('progress', handleProgress);
      progressHandlerRef.current = null;
    };
  }, [socket, jobId]);

  const reset = useCallback(() => {
    if (socket && progressHandlerRef.current) {
      socket.off('progress', progressHandlerRef.current);
      progressHandlerRef.current = null;
    }
    setStage('input');
    setJobId(null);
    setDiscoveredUrls([]);
    setTotalDiscovered(0);
    setSelectedUrls(new Set());
    setProgress({});
    setDiscoveryLogs([]);
    setConversionLogs([]);
    setError(null);
  }, [socket]);

  return {
    jobId, setJobId, stage, setStage, queuePosition, setQueuePosition,
    discoveredUrls, totalDiscovered, selectedUrls, setSelectedUrls,
    progress, setProgress, discoveryLogs, conversionLogs, error, setError, reset,
  };
}

// ── InputStage ────────────────────────────────────────────────────────────────
function InputStage({ onSubmit, error }) {
  const { t } = useTranslation();
  const [seedUrl, setSeedUrl]       = useState('');
  const [options, setOptions]       = useState({ maxDepth: 1, delay: 2000, excludePatterns: [] });
  const [scopeMode, setScopeMode]   = useState('full');
  const [pathPrefix, setPathPrefix] = useState('/');

  const prefixOptions = useMemo(() => extractPathPrefixes(seedUrl), [seedUrl]);

  useEffect(() => {
    if (!prefixOptions.includes(pathPrefix)) {
      setPathPrefix('/');
      setScopeMode('full');
    }
  }, [prefixOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveOptions = useMemo(() => ({
    ...options,
    ...(scopeMode === 'prefix' && pathPrefix && pathPrefix !== '/' ? { pathPrefix } : {})
  }), [options, scopeMode, pathPrefix]);

  const fullScopeSummary   = (() => { try { return new URL(seedUrl).origin + '/*'; }             catch { return '/*'; } })();
  const prefixScopeSummary = (() => { try { return new URL(seedUrl).origin + pathPrefix + '*'; } catch { return pathPrefix + '*'; } })();

  const handleSubmit = (e) => { e.preventDefault(); onSubmit(seedUrl, effectiveOptions); };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-4">{t('input.heading')}</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('input.urlLabel')}</label>
          <input
            type="url" value={seedUrl} onChange={(e) => setSeedUrl(e.target.value)}
            placeholder={t('input.urlPlaceholder')} className="input" required
          />
        </div>

        {prefixOptions.length > 0 && (
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
            <p className="text-sm font-medium text-gray-700">{t('input.crawlScope')}</p>
            <div className="flex gap-2">
              <button type="button" onClick={() => setScopeMode('full')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium border transition-colors duration-150 ${scopeMode === 'full' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400 hover:text-primary-600'}`}>
                {t('input.entireSite')}
              </button>
              <button type="button" onClick={() => setScopeMode('prefix')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium border transition-colors duration-150 ${scopeMode === 'prefix' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400 hover:text-primary-600'}`}>
                {t('input.fromPrefix')}
              </button>
            </div>

            {scopeMode === 'prefix' && (
              <div>
                <p className="text-xs text-gray-500 mb-2">{t('input.prefixHint')}</p>
                <div className="flex flex-wrap gap-2">
                  {prefixOptions.filter(p => p !== '/').map((p) => (
                    <button key={p} type="button" onClick={() => setPathPrefix(p)}
                      className={`py-1 px-3 rounded-full text-xs font-mono font-medium border transition-colors duration-150 ${pathPrefix === p ? 'bg-primary-100 text-primary-700 border-primary-400' : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400 hover:text-primary-600'}`}>
                      {p}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  {t('input.willCrawl')}{' '}<span className="font-mono text-primary-700">{prefixScopeSummary}</span>
                </p>
              </div>
            )}

            {scopeMode === 'full' && (
              <p className="text-xs text-gray-500">
                {t('input.willCrawl')}{' '}<span className="font-mono text-primary-700">{fullScopeSummary}</span>
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('input.maxDepth')}</label>
            <input type="number" value={options.maxDepth}
              onChange={(e) => setOptions({...options, maxDepth: parseInt(e.target.value)})}
              min="1" max="10" className="input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('input.delay')}</label>
            <input type="number" value={options.delay}
              onChange={(e) => setOptions({...options, delay: parseInt(e.target.value)})}
              min="1000" max="5000" step="500" className="input" />
          </div>
        </div>

        {error && error !== 'busy' && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
        )}
        <button type="submit" className="btn btn-primary w-full">{t('input.startDiscovery')}</button>
      </form>
    </div>
  );
}

// ── QueuedStage ───────────────────────────────────────────────────────────────
function QueuedStage({ position }) {
  const { t } = useTranslation();
  return (
    <div className="py-4">
      <div className="flex items-center gap-3 mb-5">
        <div className="flex-shrink-0 animate-spin rounded-full h-6 w-6 border-2 border-primary-200 border-t-primary-600"></div>
        <h2 className="text-xl font-semibold text-gray-900">{t('queued.heading')}</h2>
      </div>
      <div className="border border-primary-100 bg-primary-50 rounded-xl px-5 py-5 text-center">
        {position > 0 ? (
          <>
            <p className="text-4xl font-bold text-primary-700 mb-1">{position}</p>
            <p className="text-sm text-primary-600 font-medium mb-3">
              {position === 1 ? t('queued.youreNext') : t('queued.jobsAhead', { count: position })}
            </p>
          </>
        ) : (
          <p className="text-sm font-medium text-primary-700 mb-3">{t('queued.startingSoon')}</p>
        )}
        <p className="text-xs text-primary-500">{t('queued.explanation')}</p>
      </div>
    </div>
  );
}

// ── BusyStage ─────────────────────────────────────────────────────────────────
// ── RateLimitedStage ──────────────────────────────────────────────────────────
function RateLimitedStage({ resetAt, onReset }) {
  const { t } = useTranslation();

  // Compute seconds remaining, refreshed every second.
  const secondsLeft = () => {
    if (!resetAt) return 3600;
    return Math.max(0, Math.round((resetAt - Date.now()) / 1000));
  };
  const [remaining, setRemaining] = useState(secondsLeft);

  useEffect(() => {
    if (remaining === 0) return;
    const timer = setInterval(() => setRemaining(secondsLeft()), 1000);
    return () => clearInterval(timer);
  }, [resetAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
  };

  // Ring progress: full circle at start, empty when remaining = 0.
  const total   = 3600;
  const r       = 28;
  const circum  = 2 * Math.PI * r;
  const offset  = circum * (1 - remaining / total);

  return (
    <div className="py-6 text-center">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 className="text-2xl font-semibold text-gray-900 mb-2">{t('rateLimited.heading')}</h2>
      <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">{t('rateLimited.explanation')}</p>

      {remaining > 0 ? (
        <>
          <div className="flex items-center justify-center mb-3">
            <div className="relative w-16 h-16">
              <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                <circle cx="32" cy="32" r={r} fill="none" stroke="#e5e7eb" strokeWidth="4" />
                <circle cx="32" cy="32" r={r} fill="none" stroke="#ef4444" strokeWidth="4"
                  strokeDasharray={circum}
                  strokeDashoffset={offset}
                  strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s linear' }} />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-red-600 leading-tight">
                {formatTime(remaining)}
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-400 mb-5">{t('rateLimited.resetsIn', { time: formatTime(remaining) })}</p>
        </>
      ) : (
        <p className="text-xs text-gray-400 mb-5">{t('rateLimited.readyNow')}</p>
      )}

      <button onClick={onReset} disabled={remaining > 0}
        className="btn btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
        {remaining > 0 ? t('rateLimited.waitButton', { time: formatTime(remaining) }) : t('rateLimited.tryNow')}
      </button>
    </div>
  );
}

// ── BusyStage ─────────────────────────────────────────────────────────────────
function BusyStage({ onRetry }) {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(30);

  useEffect(() => {
    if (countdown === 0) { onRetry(); return; }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, onRetry]);

  return (
    <div className="py-6 text-center">
      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-5">
        <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 className="text-2xl font-semibold text-gray-900 mb-2">{t('busy.heading')}</h2>
      <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">{t('busy.explanation')}</p>
      <div className="flex items-center justify-center mb-6">
        <div className="relative w-16 h-16">
          <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
            <circle cx="32" cy="32" r="28" fill="none" stroke="#e5e7eb" strokeWidth="4" />
            <circle cx="32" cy="32" r="28" fill="none" stroke="#f59e0b" strokeWidth="4"
              strokeDasharray={`${2 * Math.PI * 28}`}
              strokeDashoffset={`${2 * Math.PI * 28 * (1 - countdown / 30)}`}
              strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s linear' }} />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-lg font-semibold text-amber-600">{countdown}</span>
        </div>
      </div>
      <p className="text-xs text-gray-400 mb-5">{t('busy.retryingIn', { count: countdown })}</p>
      <button onClick={onRetry} className="btn btn-primary">{t('busy.tryNow')}</button>
    </div>
  );
}

// ── DiscoveryStage ────────────────────────────────────────────────────────────
function DiscoveryStage({ progress, logs }) {
  const { t } = useTranslation();
  const logEndRef = useRef(null);
  useEffect(() => { logEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [logs.length]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <div className="flex-shrink-0 animate-spin rounded-full h-6 w-6 border-2 border-primary-200 border-t-primary-600"></div>
        <h2 className="text-xl font-semibold text-gray-900">{t('discovery.heading')}</h2>
      </div>
      {progress.message && (
        <div className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-lg px-4 py-2 mb-4 font-mono truncate">
          {progress.message}
        </div>
      )}
      {logs.length > 0 && (
        <div className="bg-gray-950 rounded-lg p-3 max-h-64 overflow-y-auto font-mono text-xs leading-relaxed">
          {logs.map((log, i) => (
            <div key={i} className="flex gap-2 py-0.5">
              <span className="flex-shrink-0 text-gray-500 select-none">
                {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
              <span className="text-gray-300 break-all">{log.message}</span>
            </div>
          ))}
          <div ref={logEndRef} />
        </div>
      )}
      <p className="text-xs text-gray-400 mt-3 text-center">{t('discovery.hint')}</p>
    </div>
  );
}

// ── LargeSiteWarningStage ─────────────────────────────────────────────────────
function LargeSiteWarningStage({ urlCount, discoveredCount, onContinue, onCancel }) {
  const { t } = useTranslation();
  const cappedAt = discoveredCount || urlCount;
  const tools = [
    { name: 'SiteSucker',     url: 'https://ricks-apps.com/osx/sitesucker/',      desc: t('largeSite.tools.sitesucker') },
    { name: 'HTTrack',        url: 'https://www.httrack.com/',                    desc: t('largeSite.tools.httrack') },
    { name: 'Wget recursive', url: 'https://www.gnu.org/software/wget/',           desc: t('largeSite.tools.wget') },
    { name: 'Cyotek WebCopy', url: 'https://www.cyotek.com/cyotek-webcopy',       desc: t('largeSite.tools.cyotek') },
  ];
  return (
    <div>
      <div className="flex items-start gap-4 mb-5">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mt-0.5">
          <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">{t('largeSite.heading')}</h2>
          <p className="text-sm text-gray-500 mt-1">
            Discovery stopped after{' '}
            <span className="font-medium text-gray-700">{cappedAt.toLocaleString()} URLs</span>
            {' '}— the actual site contains many more pages.
          </p>
        </div>
      </div>
      <p className="text-sm text-gray-700 mb-3">{t('largeSite.explanation')}</p>
      <ul className="space-y-2 mb-6 pl-1">
        {tools.map(({ name, url, desc }) => (
          <li key={name} className="flex items-start gap-2 text-sm">
            <svg className="flex-shrink-0 w-4 h-4 mt-0.5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
            </svg>
            <span>
              <a href={url} target="_blank" rel="noreferrer" className="font-medium text-primary-600 hover:underline">{name}</a>
              {' — '}{desc}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex flex-col sm:flex-row gap-3">
        <button onClick={onCancel}   className="btn btn-secondary flex-1">{t('largeSite.startOver')}</button>
        <button onClick={onContinue} className="btn btn-primary flex-1">{t('largeSite.browseUrls', { count: cappedAt.toLocaleString() })}</button>
      </div>
    </div>
  );
}

// ── SelectionStage ────────────────────────────────────────────────────────────
function SelectionStage({ discoveredUrls, selectedUrls, setSelectedUrls, onStartConversion, onReset, error }) {
  const { t } = useTranslation();
  const [urlFilter, setUrlFilter] = useState('');

  const filteredUrls = useMemo(() => {
    if (!urlFilter.trim()) return discoveredUrls;
    const q = urlFilter.trim().toLowerCase();
    return discoveredUrls.filter(u => {
      try { return new URL(u).pathname.toLowerCase().includes(q); }
      catch { return u.toLowerCase().includes(q); }
    });
  }, [discoveredUrls, urlFilter]);

  const filteredSelectedCount = useMemo(
    () => filteredUrls.filter(u => selectedUrls.has(u)).length,
    [filteredUrls, selectedUrls]
  );

  const toggleUrlSelection = (url) => {
    setSelectedUrls(prev => { const next = new Set(prev); next.has(url) ? next.delete(url) : next.add(url); return next; });
  };
  const selectAll   = () => { setSelectedUrls(prev => { const next = new Set(prev); filteredUrls.forEach(u => next.add(u));    return next; }); };
  const deselectAll = () => { setSelectedUrls(prev => { const next = new Set(prev); filteredUrls.forEach(u => next.delete(u)); return next; }); };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-4">{t('selection.heading')}</h2>
      <p className="text-gray-600 mb-3">
        {t(`selection.found${discoveredUrls.length !== 1 ? '_plural' : ''}`, { count: discoveredUrls.length })
          .replace('<strong>', '').replace('</strong>', '')}
        {/* Use dangerouslySetInnerHTML for the bold count */}
        {' '}<span className="font-medium text-primary-700">{selectedUrls.size}</span> {t('selection.selected', { count: selectedUrls.size }).replace(/<\/?strong>/g, '')}
        {urlFilter && filteredUrls.length !== discoveredUrls.length && (
          <span className="text-gray-500"> {t('selection.visible', { count: filteredUrls.length })}</span>
        )}
      </p>

      <div className="mb-3 flex items-center gap-3">
        <label className="flex items-center gap-2 flex-shrink-0 cursor-pointer select-none">
          <input type="checkbox" className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
            checked={filteredUrls.length > 0 && filteredSelectedCount === filteredUrls.length}
            ref={el => { if (el) el.indeterminate = filteredSelectedCount > 0 && filteredSelectedCount < filteredUrls.length; }}
            onChange={e => e.target.checked ? selectAll() : deselectAll()} />
          <span className="text-sm text-gray-700 whitespace-nowrap">
            {filteredSelectedCount === filteredUrls.length && filteredUrls.length > 0 ? t('selection.deselectAll') : t('selection.selectAll')}
            {urlFilter && filteredUrls.length !== discoveredUrls.length ? t('selection.visible_suffix') : ''}
          </span>
        </label>
        <input type="text" value={urlFilter} onChange={e => setUrlFilter(e.target.value)}
          placeholder={t('selection.filterPlaceholder')} className="input py-1.5 text-sm" />
        {urlFilter && (
          <button type="button" onClick={() => setUrlFilter('')}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 text-lg leading-none" aria-label="Clear filter">×</button>
        )}
      </div>

      <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg mb-4">
        {filteredUrls.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">
            {t('selection.noMatch', { filter: urlFilter })}
          </p>
        ) : (
          filteredUrls.map((url) => (
            <label key={url} className="flex items-center space-x-3 px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
              <input type="checkbox" checked={selectedUrls.has(url)} onChange={() => toggleUrlSelection(url)}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 flex-shrink-0" />
              <span className="text-sm text-gray-700 break-all">{url}</span>
            </label>
          ))
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>
      )}
      <div className="flex space-x-4">
        <button onClick={onReset} className="btn btn-secondary flex-1">{t('selection.cancel')}</button>
        <button onClick={onStartConversion} className="btn btn-primary flex-1" disabled={selectedUrls.size === 0}>
          {t(`selection.convert${selectedUrls.size !== 1 ? '_plural' : ''}`, { count: selectedUrls.size })}
        </button>
      </div>
    </div>
  );
}

// ── ProcessingStage ───────────────────────────────────────────────────────────
function ProcessingStage({ progress, logs }) {
  const { t } = useTranslation();
  const logEndRef = useRef(null);
  useEffect(() => { logEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [logs.length]);
  const pct = progress.percentage ?? 0;

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <div className="flex-shrink-0 animate-spin rounded-full h-6 w-6 border-2 border-primary-200 border-t-primary-600"></div>
        <h2 className="text-xl font-semibold text-gray-900">{t('processing.heading')}</h2>
      </div>
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>
            {progress.current && progress.total
              ? t('processing.pageOf', { current: progress.current, total: progress.total })
              : t('processing.starting')}
          </span>
          <span>{pct}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="progress-bar" style={{ width: `${pct}%` }}></div>
        </div>
      </div>
      {progress.message && (
        <div className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-lg px-4 py-2 mb-4 font-mono truncate">
          {progress.message}
        </div>
      )}
      {logs.length > 0 && (
        <div className="bg-gray-900 text-gray-100 rounded-lg p-4 max-h-64 overflow-y-auto font-mono text-sm">
          {logs.map((log, index) => (
            <div key={index} className="mb-1">
              <span className="text-gray-500">[{new Date(log.timestamp).toLocaleTimeString()}]</span>{' '}
              <span className={log.status === 'success' ? 'text-green-400' : log.status === 'failed' ? 'text-red-400' : 'text-gray-300'}>
                {log.message}
              </span>
            </div>
          ))}
          <div ref={logEndRef} />
        </div>
      )}
    </div>
  );
}

// ── Donation platforms ────────────────────────────────────────────────────────
const DONATION_PLATFORMS = [
  {
    key: 'kofi', url: import.meta.env.VITE_KOFI_URL, label: 'Ko-fi',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8h1a4 4 0 010 8h-1" /><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z" />
        <line x1="6" y1="1" x2="6" y2="4" /><line x1="10" y1="1" x2="10" y2="4" /><line x1="14" y1="1" x2="14" y2="4" />
      </svg>
    ),
  },
  {
    key: 'bmc', url: import.meta.env.VITE_BUYMEACOFFEE_URL, label: 'Buy me a coffee',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 884 1279" fill="currentColor">
        <path d="M791.109 297.518L790.5 297C790.891 297.338 791.109 297.518 791.109 297.518V297.518Z"/>
        <path d="M525 1079C525 1079 543 1091 549 1096C579 1116 617 1128 645 1132C673 1136 695 1132 717 1124C739 1116 757 1104 761 1098L525 1079Z"/>
        <path d="M746.06 1254.88L454.75 1268.74C439.754 1269.46 425.911 1261.93 418.491 1249.12L37.0357 584.512C29.3977 571.364 29.6407 555.052 37.6557 542.16L129.516 391.044C135.847 380.913 146.849 374.726 158.666 374.726H574.826C582.432 374.726 589.832 377.273 595.786 381.956L750.066 507.766C762.76 517.953 766.553 535.718 759.142 550.123L547.266 964.741C541.787 975.356 541.749 988.044 547.152 998.686L786.252 1231.11C797.576 1242.44 794.851 1261.37 781.052 1268.73L746.06 1254.88Z"/>
        <path d="M455.5 468C481.474 468 504 452.303 504 432.741V155.259C504 135.697 481.474 120 455.5 120C429.526 120 407 135.697 407 155.259V432.741C407 452.303 429.526 468 455.5 468Z"/>
      </svg>
    ),
  },
  {
    key: 'paypal', url: import.meta.env.VITE_PAYPAL_ME_URL, label: 'PayPal',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.84A.78.78 0 0 1 5.717 2h8.28c2.884 0 4.984 1.095 5.403 3.584.156.923.118 1.789-.072 2.588-.838 3.555-3.715 4.876-7.175 4.876H9.867a.781.781 0 0 0-.772.66l-.776 4.913-.243 1.716zm10.55-14.048c-.057.354-.127.7-.213 1.04-.9 3.643-3.75 4.59-6.894 4.59H8.886a.641.641 0 0 0-.633.54l-.845 5.365-.24 1.524a.378.378 0 0 0 .374.434h2.632a.54.54 0 0 0 .534-.455l.022-.115.422-2.674.027-.146a.54.54 0 0 1 .534-.456h.336c2.18 0 3.886-.885 4.384-3.446.208-1.072.1-1.967-.452-2.597a2.15 2.15 0 0 0-.616-.444"/>
      </svg>
    ),
  },
  {
    key: 'github', url: import.meta.env.VITE_GITHUB_SPONSORS_URL, label: 'GitHub Sponsors',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0 1 12 6.844a9.59 9.59 0 0 1 2.504.337c1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.02 10.02 0 0 0 22 12.017C22 6.484 17.522 2 12 2z"/>
      </svg>
    ),
  },
].filter(p => p.url && p.url.trim() !== '');

// ── FeedbackForm ─────────────────────────────────────────────────────────────
function FeedbackForm({ convertedUrl }) {
  const { t } = useTranslation();
  const [open,     setOpen]     = useState(false);
  const [rating,   setRating]   = useState(null);
  const [category, setCategory] = useState('other');
  const [message,  setMessage]  = useState('');
  const [status,   setStatus]   = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) return;
    setStatus('sending');
    try {
      await axios.post('/api/feedback', { rating, category, message: message.trim(), url: convertedUrl || '' });
      setStatus('done');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <div className="mt-6 pt-6 border-t border-gray-100 text-center">
        <p className="text-sm text-green-700 font-medium">{t('feedback.thanks')}</p>
      </div>
    );
  }

  const categories = [
    { value: 'bug',        label: t('feedback.popup') },
    { value: 'bug',        label: t('feedback.contentMissing') },
    { value: 'bug',        label: t('feedback.conversionFailed') },
    { value: 'suggestion', label: t('feedback.suggestion') },
    { value: 'other',      label: t('feedback.other') },
  ];

  return (
    <div className="mt-5">
      {!open ? (
        <button onClick={() => setOpen(true)}
          className="w-full text-xs text-gray-400 hover:text-gray-600 flex items-center justify-center gap-1.5 py-1 transition-colors">
          <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
          </svg>
          {t('feedback.trigger')}
        </button>
      ) : (
        <div className="border border-primary-100 bg-primary-50 rounded-xl px-5 py-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm font-medium text-gray-700">{t('feedback.heading')}</p>
            <div className="flex gap-3">
              {[
                { value: 'positive', label: t('feedback.positive'), icon: '👍' },
                { value: 'negative', label: t('feedback.negative'), icon: '👎' },
              ].map(({ value, label, icon }) => (
                <button key={value} type="button" onClick={() => setRating(value)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                    rating === value
                      ? value === 'positive' ? 'border-green-500 bg-green-50 text-green-700' : 'border-red-400 bg-red-50 text-red-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                  <span className="text-base">{icon}</span> {label}
                </button>
              ))}
            </div>
            {rating === 'negative' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('feedback.whatWrong')}</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map(({ value, label }) => (
                    <button key={label} type="button" onClick={() => setCategory(value)}
                      className={`py-1 px-3 rounded-full text-xs border transition-colors ${
                        category === value && message === '' ? 'bg-primary-100 text-primary-700 border-primary-400' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <textarea value={message} onChange={e => setMessage(e.target.value)}
              placeholder={rating === 'negative' ? t('feedback.placeholderNegative') : t('feedback.placeholderPositive')}
              rows={3} maxLength={2000} className="input resize-none text-sm" />
            {status === 'error' && <p className="text-xs text-red-600">{t('feedback.error')}</p>}
            <div className="flex gap-3">
              <button type="button" onClick={() => setOpen(false)} className="btn btn-secondary text-sm flex-1">{t('feedback.cancel')}</button>
              <button type="submit" disabled={!rating || status === 'sending'} className="btn btn-primary text-sm flex-1">
                {status === 'sending' ? t('feedback.sending') : t('feedback.send')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ── CompletedStage ────────────────────────────────────────────────────────────
function CompletedStage({ progress, onDownload, onReset, convertedUrl }) {
  const { t } = useTranslation();
  return (
    <div className="py-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-semibold mb-2">{t('completed.heading')}</h2>
        {progress.results && (
          <div className="text-gray-600 mb-1">
            <p>
              Successfully converted{' '}
              <span className="font-medium text-gray-900">{progress.results.successful}</span> of{' '}
              <span className="font-medium text-gray-900">{progress.results.total}</span> pages
            </p>
            {progress.results.failed > 0 && (
              <p className="text-yellow-600 mt-1 text-sm">
                {t(`completed.failedCount${progress.results.failed !== 1 ? '_plural' : ''}`, { count: progress.results.failed })}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Expiry notice */}
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-5 text-left">
        <svg className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        <div>
          <p className="text-sm font-medium text-amber-800">{t('completed.expiryHeading')}</p>
          <p className="text-xs text-amber-700 mt-0.5">{t('completed.expiryBody')}</p>
        </div>
      </div>

      <div className="flex gap-3 justify-center mb-6">
        <button onClick={onDownload} className="btn btn-primary">{t('completed.download')}</button>
        <button onClick={onReset}    className="btn btn-secondary">{t('completed.convertAnother')}</button>
      </div>
      <FeedbackForm convertedUrl={convertedUrl} />
      {DONATION_PLATFORMS.length > 0 && <hr className="my-6 border-gray-100" />}
      {DONATION_PLATFORMS.length > 0 && (
        <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/60 px-5 py-3.5 text-center">
          <p className="text-xs text-blue-700/80 mb-2.5">
            <svg className="w-3 h-3 inline-block mr-1 mb-0.5 text-red-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            {t('donation.tagline')}
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            {DONATION_PLATFORMS.map(({ key, url, label, icon }) => (
              <a key={key} href={url} target="_blank" rel="noreferrer noopener"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-blue-700 bg-white border border-blue-200 hover:bg-blue-50 hover:border-blue-300 transition-colors">
                {icon}{label}
              </a>
            ))}
          </div>
          <p className="text-xs text-blue-500/60 mt-2.5 italic">{t('donation.cta')}</p>
        </div>
      )}
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
function App() {
  const { t } = useTranslation();
  const {
    jobId, setJobId, stage, setStage, queuePosition, setQueuePosition,
    discoveredUrls, totalDiscovered, selectedUrls, setSelectedUrls,
    progress, setProgress, discoveryLogs, conversionLogs,
    error, setError, reset,
  } = useConversionJob();

  const jobParamsRef = useRef(null);

  const handleSubmitWithParams = async (seedUrl, effectiveOptions) => {
    jobParamsRef.current = { seedUrl, options: effectiveOptions };
    setError(null);
    try {
      const response = await axios.post('/api/convert', { seedUrl, options: effectiveOptions });
      const { jobId: newJobId, queuePosition: pos } = response.data;
      setJobId(newJobId);
      if (pos > 0) { setQueuePosition(pos); setStage('queued'); } else { setStage('discovery'); }
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 503 && data?.busy) {
        setStage('busy');
      } else if (err.response?.status === 429) {
        // Parse the reset time from the RateLimit-Reset header (Unix seconds).
        const resetHeader = err.response?.headers?.['ratelimit-reset'];
        const resetAt = resetHeader ? parseInt(resetHeader) * 1000 : Date.now() + 3600 * 1000;
        setError(resetAt);     // reuse error field to carry resetAt timestamp
        setStage('rate-limited');
      } else {
        setError(data?.error || 'Failed to start conversion');
      }
    }
  };

  const handleStartConversion = async () => {
    const { seedUrl, options } = jobParamsRef.current || {};
    try {
      setProgress({});
      setStage('processing');
      await axios.post(`/api/convert/${jobId}/start`, { seedUrl, options, selectedUrls: Array.from(selectedUrls) });
    } catch (err) {
      setStage('selection');
      setError(err.response?.data?.error || 'Failed to start conversion');
    }
  };

  const handleDownload = () => { window.location.href = `/api/download/${jobId}`; };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">

        {/* Header with language switcher */}
        <div className="relative text-center mb-8">
          <div className="absolute right-0 top-1">
            <LanguageSwitcher />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('header.title')}</h1>
          <p className="text-gray-600">{t('header.subtitle')}</p>
        </div>

        <div className="card">
          {stage === 'input'             && <InputStage onSubmit={handleSubmitWithParams} error={error} />}
          {stage === 'queued'            && <QueuedStage position={queuePosition} />}
          {stage === 'rate-limited'      && <RateLimitedStage resetAt={error} onReset={() => { setError(null); setStage('input'); }} />}
          {stage === 'busy'              && <BusyStage onRetry={() => { setError(null); setStage('input'); }} />}
          {stage === 'discovery'         && <DiscoveryStage progress={progress} logs={discoveryLogs} />}
          {stage === 'large-site-warning'&& <LargeSiteWarningStage urlCount={discoveredUrls.length} discoveredCount={totalDiscovered} onContinue={() => setStage('selection')} onCancel={reset} />}
          {stage === 'selection'         && <SelectionStage discoveredUrls={discoveredUrls} selectedUrls={selectedUrls} setSelectedUrls={setSelectedUrls} onStartConversion={handleStartConversion} onReset={reset} error={error} />}
          {stage === 'processing'        && <ProcessingStage progress={progress} logs={conversionLogs} />}
          {stage === 'completed'         && <CompletedStage progress={progress} onDownload={handleDownload} onReset={reset} convertedUrl={jobParamsRef.current?.seedUrl} />}
        </div>

        <div className="text-center mt-8 text-gray-600 text-sm">
          <p>{t('footer')}</p>
        </div>
      </div>
    </div>
  );
}

export default App;

// Made with Bob
